import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  Legend,
} from "recharts";

// TypeScript interfaces
export interface OutageDataPoint {
  date: string;
  hour_ending: number;
  [key: string]: number | string; // Allow dynamic fuel type properties
}

export interface OutageChartProps {
  data: OutageDataPoint[];
  fuelTypes: string[]; // Array of fuel type keys to display
  fuelTypeLabels?: { [key: string]: string }; // Optional mapping for display labels
  unit?: string;
  currentTime?: string; // ISO timestamp for current time divider
  title?: string; // Optional custom title
}

// Schema to display name mapping
const schemaToDisplayMap: { [key: string]: string } = {
  wind_solar: "Wind + Solar",
  thermal: "Thermal",
  nuclear: "Nuclear",
  hydro: "Hydro",
  coal_and_lignite: "Coal And Lignite",
  natural_gas: "Natural Gas",
  wind: "Wind",
  power_storage: "Power Storage",
  solar: "Solar",
  other: "Other"
};

// Generate mock data for wind + solar
export const generateMockWindSolarData = (): OutageDataPoint[] => {
  const data: OutageDataPoint[] = [];
  const startDate = new Date('2025-06-13');
  
  // Generate data for 7 days, 24 hours each day (but only every 15 minutes to match realistic data)
  for (let day = 0; day < 7; day++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + day);
    const dateStr = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    // Generate data every 15 minutes (4 points per hour)
    for (let hour = 0; hour < 24; hour++) {
      for (let quarter = 0; quarter < 4; quarter++) {
        let windSolarValue: number;
        const minute = quarter * 15;
        const decimalHour = hour + (minute / 60);
        
        // Create realistic wind + solar generation patterns
        if (day < 4) {
          // Historical data (more stable with some variation)
          if (hour >= 6 && hour <= 18) {
            // Daytime with solar
            windSolarValue = 4200 + Math.random() * 800 + Math.sin((hour - 6) / 12 * Math.PI) * 600;
          } else {
            // Nighttime (mostly wind)
            windSolarValue = 4000 + Math.random() * 600;
          }
        } else {
          // Forecast data (day 4 = current day transition, days 5-6 = outlook)
          if (day === 4 && hour <= 12) {
            // Current day morning (historical)
            windSolarValue = 4800 + Math.random() * 400;
          } else {
            // Forecast/outlook data with planned outages showing the drop visible in the image
            if (day >= 5) {
              // Major outage period - significant drop
              windSolarValue = 3600 + Math.random() * 400;
            } else {
              // Transition period
              windSolarValue = 4200 + Math.random() * 500;
            }
          }
        }
        
        data.push({
          date: dateStr,
          hour_ending: decimalHour,
          wind_solar: Math.round(windSolarValue)
        });
      }
    }
  }
  
  return data;
};

// Generate mock data for thermal
export const generateMockThermalData = (): OutageDataPoint[] => {
  const data: OutageDataPoint[] = [];
  const startDate = new Date('2025-06-13');
  
  // Generate data for 7 days, 24 hours each day (but only every 15 minutes to match realistic data)
  for (let day = 0; day < 7; day++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + day);
    const dateStr = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    // Generate data every 15 minutes (4 points per hour)
    for (let hour = 0; hour < 24; hour++) {
      for (let quarter = 0; quarter < 4; quarter++) {
        let thermalValue: number;
        const minute = quarter * 15;
        const decimalHour = hour + (minute / 60);
        
        // Create realistic thermal generation patterns (more stable baseline)
        if (day < 4) {
          // Historical data
          thermalValue = 25000 + Math.random() * 3000 + Math.sin(hour / 24 * Math.PI * 2) * 2000;
        } else {
          // Forecast data
          if (day === 4 && hour <= 12) {
            // Current day morning (historical)
            thermalValue = 26000 + Math.random() * 2000;
          } else {
            // Forecast/outlook data with planned maintenance
            if (day >= 5) {
              // Maintenance period - some reduction
              thermalValue = 22000 + Math.random() * 2500;
            } else {
              // Transition period
              thermalValue = 24000 + Math.random() * 3000;
            }
          }
        }
        
        data.push({
          date: dateStr,
          hour_ending: decimalHour,
          thermal: Math.round(thermalValue)
        });
      }
    }
  }
  
  return data;
};

export const mockWindSolarData = generateMockWindSolarData();
export const mockThermalData = generateMockThermalData();

const OutageChart: React.FC<OutageChartProps> = ({
  data,
  fuelTypes,
  fuelTypeLabels = {},
  unit = "MW",
  currentTime = "2025-06-16T12:00:00Z",
  title
}) => {
  // Preprocess data to use display names as keys
  const preprocessedData = React.useMemo(() => {
    const currentTimestamp = new Date(currentTime);
    
    return data.map(point => {
      // Create timestamp from date + hour_ending
      const timestamp = new Date(point.date);
      timestamp.setHours(Math.floor(point.hour_ending), (point.hour_ending % 1) * 60, 0, 0);
      
      // Create new point with display names as keys
      const newPoint: any = {
        date: point.date,
        hour_ending: point.hour_ending,
        timestamp: timestamp.toISOString(),
        timestampMs: timestamp.getTime(),
        isForecast: timestamp > currentTimestamp,
        displayDate: `${timestamp.getMonth() + 1}/${timestamp.getDate()}`
      };
      
      // Map fuel types to display names
      fuelTypes.forEach(fuelType => {
        const displayName = fuelTypeLabels[fuelType] || schemaToDisplayMap[fuelType] || fuelType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
        const value = (point as any)[fuelType];
        if (value !== undefined) {
          newPoint[displayName] = value;
        }
      });
      
      return newPoint;
    });
  }, [data, fuelTypes, fuelTypeLabels, currentTime]);

  // Get display names for the fuel types
  const displayNames = React.useMemo(() => {
    return fuelTypes.map(fuelType => 
      fuelTypeLabels[fuelType] || schemaToDisplayMap[fuelType] || fuelType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    );
  }, [fuelTypes, fuelTypeLabels]);

  // Get min/max timestamps for chart domain
  const minTimestamp = Math.min(...preprocessedData.map(d => d.timestampMs));
  const maxTimestamp = Math.max(...preprocessedData.map(d => d.timestampMs));
  const currentTimestamp = new Date(currentTime).getTime();

  // Generate daily ticks (one per day at midnight)
  const dailyTicks = React.useMemo(() => {
    const ticks = [];
    const startDate = new Date(minTimestamp);
    const endDate = new Date(maxTimestamp);
    
    // Start from the first day at midnight
    const currentTick = new Date(startDate);
    currentTick.setHours(0, 0, 0, 0);
    
    while (currentTick <= endDate) {
      ticks.push(currentTick.getTime());
      currentTick.setDate(currentTick.getDate() + 1);
    }
    
    return ticks;
  }, [minTimestamp, maxTimestamp]);

  // Calculate dynamic Y-axis domain and ticks
  const yAxisConfig = React.useMemo(() => {
    // Find the maximum value across all display names
    const maxValue = Math.max(...preprocessedData.map(point => 
      Math.max(...displayNames.map(displayName => Number(point[displayName]) || 0))
    ));
    
    // Calculate appropriate interval (must be product of 1000)
    // Start with 1000 and increase until we have reasonable number of ticks (4-8)
    let interval = 1000;
    while ((maxValue / interval) > 8) {
      if (interval === 1000) interval = 2000;
      else if (interval === 2000) interval = 5000;
      else if (interval === 5000) interval = 10000;
      else if (interval === 10000) interval = 20000;
      else if (interval === 20000) interval = 25000;
      else if (interval === 25000) interval = 50000;
      else interval *= 2;
    }
    
    // Calculate the ceiling (highest tick that contains the max value)
    const maxTick = Math.ceil(maxValue / interval) * interval;
    
    // Generate ticks from 0 to maxTick
    const ticks = [];
    for (let i = 0; i <= maxTick; i += interval) {
      ticks.push(i);
    }
    
    return {
      domain: [0, maxTick],
      ticks,
      interval
    };
  }, [preprocessedData, displayNames]);

  // Get colors for fuel types
  const getFuelTypeColor = (fuelType: string) => {
    if (fuelType === 'wind_solar') return '#10b981';
    if (fuelType === 'thermal') return '#f59e0b';
    // Default colors for other fuel types
    const colors = ['#3b82f6', '#ef4444', '#8b5cf6', '#f97316', '#06b6d4'];
    const index = fuelTypes.indexOf(fuelType) % colors.length;
    return colors[index];
  };

  // Get color for display name
  const getDisplayNameColor = (displayName: string) => {
    // Find the original fuel type for this display name
    const fuelType = fuelTypes.find(ft => {
      const mappedName = fuelTypeLabels[ft] || schemaToDisplayMap[ft] || ft.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
      return mappedName === displayName;
    });
    return fuelType ? getFuelTypeColor(fuelType) : '#6b7280';
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      const timestamp = new Date(dataPoint.timestamp);
      const dateStr = timestamp.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
      const timeStr = timestamp.toLocaleTimeString('en-US', { 
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      return (
        <div className="bg-white dark:bg-[#2D3748] border border-gray-300 dark:border-gray-600 rounded p-1.5 shadow-lg opacity-80 text-xs max-w-xs">
          <p className="font-semibold text-neutral-600 dark:text-[#CEE0EF] mb-1 text-xs leading-tight">
            {dateStr} {timeStr} CDT
          </p>
          {displayNames.map(displayName => {
            const value = dataPoint[displayName];
            if (value !== undefined) {
              return (
                <div key={displayName} className="flex items-center mb-0.5 last:mb-0">
                  {/* COLOR DOT */}
                  <span
                    style={{
                      backgroundColor: getDisplayNameColor(displayName),
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: "4px",
                      flexShrink: 0,
                    }}
                  ></span>

                  {/* TEXT */}
                  <span className="text-xs text-neutral-600 dark:text-[#CEE0EF] leading-tight">
                    {displayName}:{" "}
                    <span className="font-semibold">
                      {value?.toLocaleString()} {unit}
                    </span>
                  </span>
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="relative w-full h-full bg-white dark:bg-[#2D3748] rounded-lg text-neutral-600 dark:text-[#CEE0EF]">   
      <ResponsiveContainer width="100%" height={350}>
        <LineChart
          data={preprocessedData}
          margin={{
            top: 20,
            right: 20,
            left: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-600" />
          
          {/* Forecast area shading - using domain coordinates */}
          <ReferenceArea
            x1={currentTimestamp}
            x2={maxTimestamp}
            fill="#64748b"
            fillOpacity={0.2}
          />
          
          {/* Current time divider line */}
          <ReferenceLine
            x={currentTimestamp}
            stroke="#06b6d4"
            strokeDasharray="8 8"
            strokeWidth={2}
            opacity={0.8}
          />
          
          <XAxis 
            dataKey="timestampMs"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "currentColor" }}
            domain={[minTimestamp, maxTimestamp]}
            type="number"
            scale="time"
            ticks={dailyTicks}
            tickFormatter={(timestamp) => {
              const date = new Date(timestamp);
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
          />
          
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "currentColor" }}
            domain={yAxisConfig.domain}
            ticks={yAxisConfig.ticks}
            tickFormatter={(value) => {
              if (value >= 1000) {
                return `${(value/1000).toFixed(0)},000 ${unit}`;
              }
              return `${value} ${unit}`;
            }}
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          <Legend 
            verticalAlign="bottom"
            height={36}
            iconType="line"
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '14px'
            }}
            formatter={(value) => (
              <span className="text-neutral-600 dark:text-[#CEE0EF]">{value}</span>
            )}
          />
          
          {displayNames.map((displayName, index) => (
            <Line
              key={displayName}
              type="monotone"
              dataKey={displayName}
              stroke={getDisplayNameColor(displayName)}
              strokeWidth={0.8}
              dot={false}
              activeDot={{ r: 4, fill: getDisplayNameColor(displayName), strokeWidth: 0 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      
      {/* Outlook label positioned at top right of forecast area */}
      <div className="absolute bottom-24 right-8 text-cyan-500 text-lg font-medium pointer-events-none opacity-55">
        Outlook
      </div>
    </div>
  );
};

export default OutageChart; 